// Coordination of rendering process: DOM insertion, transitions, pagination

import { handleAppendResults, handleMorphTransition } from './morphing-engine.js';
import { initSummaryEvents } from './summary-handler.js';
import { renderFulltextPagination } from '../pagination.js';
import { initPullToLoad } from '../pull-to-load/pull-to-load.js';

/**
 * Handle scroll to top for new queries
 * @param {Object} data - Search results data
 * @param {boolean} isAppendingResults - Whether appending results
 */
function handleScrollToTop(data, isAppendingResults) {
    const isNewQuery = !window.__lastRenderedQuery || window.__lastRenderedQuery !== (data && data.query);
    if (isNewQuery && !window.__skipScrollAfterRender && !isAppendingResults) {
        window.__lastRenderedQuery = (data && data.query) || '';
        setTimeout(() => {
            const containerEl = document.getElementById('nav-full-text');
            if (containerEl) {
                const rect = containerEl.getBoundingClientRect();
                const currentScrollY = window.scrollY || window.pageYOffset || 0;
                const containerTop = rect.top + currentScrollY;
                if (currentScrollY > containerTop) {
                    containerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        }, 150);
    }
    
    if (!isAppendingResults) window.__skipScrollAfterRender = false;
}

/**
 * Render pagination and pull-to-load
 * @param {Object} data - Search results data
 * @param {number} total - Total results count
 * @param {boolean} isAppendingResults - Whether appending results
 */
function renderPaginationAndPullToLoad(data, total, isAppendingResults) {
    try { 
        renderFulltextPagination(
            data && data.pagination, 
            (data && data.query) || (window.QUERY || ''), 
            total
        );
        if (data && data.pagination && !isAppendingResults) {
            initPullToLoad(
                data.pagination, 
                (data && data.query) || (window.QUERY || '')
            );
        }
    } catch(__) {}
}

/**
 * Coordinate rendering of results into DOM
 * @param {Object} params - Rendering parameters
 * @param {HTMLElement} params.resultsContainerEl - Results container element
 * @param {DocumentFragment} params.frag - Document fragment with cards
 * @param {jQuery} params.$allExistingResults - Existing results jQuery collection
 * @param {Object} params.data - Search results data
 * @param {number} params.total - Total results count
 * @param {boolean} params.isAppendingResults - Whether appending results
 */
export async function coordinateRendering({ 
    resultsContainerEl, 
    frag, 
    $allExistingResults, 
    data, 
    total, 
    isAppendingResults 
}) {
    const hasOldResults = $allExistingResults.length > 0;
    
    if (isAppendingResults && hasOldResults) {
        await handleAppendResults(resultsContainerEl, frag, $allExistingResults);
        
        // Re-run pagination and pull-to-load after append
        setTimeout(() => {
            try { 
                renderFulltextPagination(
                    data && data.pagination, 
                    (data && data.query) || (window.QUERY || ''), 
                    total
                );
                if (data && data.pagination) {
                    initPullToLoad(
                        data.pagination, 
                        (data && data.query) || (window.QUERY || '')
                    );
                }
            } catch(__) {}
        }, 0);
        
        // Re-init summary events to be sure
        initSummaryEvents();
    } else if (hasOldResults) {
        handleMorphTransition(resultsContainerEl, frag, $allExistingResults, data);
        initSummaryEvents();
    } else {
        resultsContainerEl.appendChild(frag);
        initSummaryEvents();
    }
    
    handleScrollToTop(data, isAppendingResults);
    renderPaginationAndPullToLoad(data, total, isAppendingResults);
}

